/**
 * Analytics and Error Tracking Utilities
 *
 * Provides simple tracking for page views, events, and errors.
 * In production, errors are logged; can be extended to send to Sentry/LogRocket.
 */

// Track custom events (Vercel Analytics handles page views automatically)
export function trackEvent(name: string, properties?: Record<string, string | number | boolean>) {
  // Vercel Analytics custom events
  if (typeof window !== 'undefined' && (window as unknown as { va?: (cmd: string, data: unknown) => void }).va) {
    (window as unknown as { va: (cmd: string, data: unknown) => void }).va('event', { name, ...properties });
  }

  // Also log in development
  if (import.meta.env.DEV) {
    console.log('[Analytics]', name, properties);
  }
}

// Pre-defined events
export const analytics = {
  // User actions
  walletConnected: (wallet: string) => trackEvent('wallet_connected', { wallet: wallet.slice(0, 8) }),
  walletDisconnected: () => trackEvent('wallet_disconnected'),
  authSigned: () => trackEvent('auth_signed'),
  authFailed: (reason: string) => trackEvent('auth_failed', { reason }),

  // Token analysis
  tokenAnalyzed: (address: string, riskLevel: string) =>
    trackEvent('token_analyzed', { address: address.slice(0, 8), riskLevel }),
  tokenViewed: (address: string, source: string) =>
    trackEvent('token_viewed', { address: address.slice(0, 8), source }),

  // Feed interactions
  feedTabChanged: (tab: string) => trackEvent('feed_tab_changed', { tab }),
  feedFiltered: (filterCount: number) => trackEvent('feed_filtered', { filterCount }),
  feedViewChanged: (view: string) => trackEvent('feed_view_changed', { view }),
  feedRefreshed: () => trackEvent('feed_refreshed'),

  // Search
  searchPerformed: (query: string) => trackEvent('search', { queryLength: query.length }),
  searchResultClicked: (position: number) => trackEvent('search_result_clicked', { position }),

  // Alerts
  alertCreated: (type: string) => trackEvent('alert_created', { type }),
  alertTriggered: (type: string) => trackEvent('alert_triggered', { type }),
  alertDismissed: () => trackEvent('alert_dismissed'),

  // Trading
  tradeSimulated: (action: string, amount: number) =>
    trackEvent('trade_simulated', { action, amount }),
  tradeLinkCopied: (platform: string) => trackEvent('trade_link_copied', { platform }),

  // Whale tracking
  walletTracked: () => trackEvent('wallet_tracked'),
  whaleActivityViewed: () => trackEvent('whale_activity_viewed'),

  // Social
  socialLinkClicked: (platform: string, tokenAddress: string) =>
    trackEvent('social_link_clicked', { platform, address: tokenAddress.slice(0, 8) }),

  // WebSocket
  wsConnected: () => trackEvent('ws_connected'),
  wsDisconnected: () => trackEvent('ws_disconnected'),
  wsReconnected: () => trackEvent('ws_reconnected'),

  // Feature usage
  featureUsed: (feature: string) => trackEvent('feature_used', { feature }),

  // Performance (for monitoring)
  pageLoadTime: (page: string, loadTimeMs: number) =>
    trackEvent('page_load_time', { page, loadTimeMs }),
  apiLatency: (endpoint: string, latencyMs: number) =>
    trackEvent('api_latency', { endpoint, latencyMs }),
};

// Error tracking
interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  extra?: Record<string, unknown>;
}

export function trackError(error: Error, context?: ErrorContext) {
  const errorData = {
    message: error.message,
    stack: error.stack?.slice(0, 500),
    ...context,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    timestamp: new Date().toISOString(),
  };

  // Always log to console
  console.error('[Error]', error.message, errorData);

  // In production, could send to error tracking service
  if (import.meta.env.PROD) {
    // Could integrate Sentry, LogRocket, etc. here
    // For now, we rely on Vercel's built-in error tracking
    trackEvent('error', {
      message: error.message.slice(0, 100),
      component: context?.component || 'unknown',
    });
  }
}

// Global error handler setup
export function setupErrorTracking() {
  if (typeof window === 'undefined') return;

  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    trackError(event.error || new Error(event.message), {
      component: 'window',
      action: 'unhandled_error',
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));

    trackError(error, {
      component: 'window',
      action: 'unhandled_rejection',
    });
  });
}
